<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import { Loader2 } from 'lucide-vue-next'
import { VisuallyHidden } from 'reka-ui'
import type { Icons } from '@/components/base/BaseIcon.vue'

const emit = defineEmits(['close', 'click', 'update:isOpen'])
const slots = useSlots()

const props = withDefaults(
  defineProps<{
    cancel?: string
    icon?: Icons
    iconColor?: string
    isClosable?: boolean
    isDisabled?: boolean
    isOpen: boolean
    isProcessing?: boolean
    linkText?: string
    size?: 'full' | '6xl' | '5xl' | '4xl' | '3xl' | '2xl' | 'xl' | 'lg' | 'md' | 'sm' | 'xs'
    textTitle?: string
    text?: string
    headingTitle?: string
    showFooter?: boolean
    contentClass?: string
    overlayClass?: string
    scrollable?: boolean
  }>(),
  {
    linkText: '',
    text: '',
    textTitle: '',
    isClosable: true,
    cancel: null,
    icon: null,
    size: '3xl',
    iconColor: 'blue',
    isProcessing: false,
    isDisabled: false,
    headingTitle: '',
    showFooter: true,
    contentClass: '',
    overlayClass: '',
    scrollable: true
  }
)

const isVisible = useVModel(props, 'isOpen')
const close = (value: boolean) => {
  isVisible.value = value
  emit('close')
}

const onInteractOutside = (event: Event) => {
  if (!props.isClosable) {
    event.preventDefault()
    return
  }

  const detail = (event as CustomEvent<{ originalEvent?: Event }>).detail
  const target = detail?.originalEvent?.target as HTMLElement | undefined
  // Portaled popovers render outside DialogContent and must not dismiss the modal
  if (target?.closest('[data-slot="popover-content"]')) {
    event.preventDefault()
  }
}

const maxWidth = computed(() => {
  switch (props.size) {
    case 'full':
      return '!w-screen !max-w-none !h-screen !max-h-none !top-0 !left-0 !translate-x-0 !translate-y-0 !rounded-none'
    case '6xl':
      return 'sm:max-w-6xl'
    case '5xl':
      return 'sm:max-w-5xl'
    case '4xl':
      return 'sm:max-w-4xl'
    case '2xl':
      return 'sm:max-w-2xl'
    case 'xl':
      return 'sm:max-w-xl'
    case 'lg':
      return 'sm:max-w-lg'
    case 'md':
      return 'sm:max-w-md'
    case 'sm':
      return 'sm:max-w-sm'
    case 'xs':
      return 'sm:max-w-xs'
    default:
      return 'sm:max-w-3xl'
  }
})

const showHeader = computed(() => Boolean(props.headingTitle) || Boolean(slots.customHeadingTitle))
</script>

<template>
  <Dialog v-model:open="isVisible" @update:open="close">
    <DialogContent
      :show-close-button="isClosable"
      :overlay-class="overlayClass"
      :class="[
        'flex flex-col',
        size === 'full' ? 'p-0 gap-0 h-full' : scrollable ? 'max-h-[90vh]' : '',
        maxWidth,
        contentClass
      ]"
      @escape-key-down="!isClosable && $event.preventDefault()"
      @interact-outside="onInteractOutside"
    >
      <DialogHeader
        v-if="showHeader"
        class="border-b border-border bg-white px-6 py-4"
        :class="size !== 'full' && '-mx-6 -mt-6 rounded-t-lg'"
      >
        <DialogTitle :class="(!headingTitle || $slots.customHeadingTitle) && 'sr-only'">
          {{ headingTitle || 'Dialog' }}
        </DialogTitle>
        <div v-if="$slots.customHeadingTitle" class="w-full">
          <div
            v-if="icon"
            class="mx-auto mt-5 flex aspect-square h-11 w-11 items-center justify-center rounded-full p-2.5 sm:mb-6"
            :class="`bg-${iconColor}-100`"
          >
            <BaseIcon :name="icon" class="h-6 w-6" :class="`text-${iconColor}-400`" />
          </div>
          <slot name="customHeadingTitle" />
        </div>
      </DialogHeader>
      <VisuallyHidden v-else>
        <DialogTitle>Dialog</DialogTitle>
        <DialogDescription>Dialog content</DialogDescription>
      </VisuallyHidden>

      <div v-if="textTitle || icon">
        <div
          v-if="icon"
          class="mx-auto mt-5 flex aspect-square h-11 w-11 items-center justify-center rounded-full p-2.5 sm:mb-6"
          :class="`bg-${iconColor}-100`"
        >
          <BaseIcon :name="icon" class="h-6 w-6" :class="`text-${iconColor}-400`" />
        </div>

        <div v-if="textTitle" class="mb-1 space-y-1.5">
          <h3 class="text-center text-base font-semibold leading-normal text-foreground">
            {{ textTitle }}
          </h3>
          <p
            v-if="text && !$slots.text"
            class="text-center text-sm font-normal leading-normal text-foreground-secondary"
          >
            {{ text }}
          </p>
          <slot name="text"></slot>
        </div>
      </div>

      <div :class="scrollable ? 'min-h-0 flex-1 overflow-x-hidden overflow-y-auto p-1' : 'p-1'">
        <slot />
      </div>

      <DialogFooter v-if="showFooter && (cancel || linkText || $slots.linkBtn)">
        <div
          v-if="cancel || linkText"
          :class="[
            'w-full flex gap-4 items-center justify-between',
            {
              'justify-end': !textTitle,
              'justify-center': textTitle
            }
          ]"
        >
          <Button
            v-if="cancel"
            id="cancelBtn"
            variant="outline"
            class="max-h-fit w-full min-w-max sm:flex-1"
            :class="!textTitle && 'sm:w-auto sm:flex-none'"
            :disabled="isProcessing"
            @click="$emit('close')"
          >
            {{ cancel }}
          </Button>
          <Button
            v-if="linkText"
            id="linkBtn"
            variant="default-primary"
            class="max-h-fit w-full min-w-max sm:flex-1"
            :class="!textTitle && 'sm:w-auto sm:flex-none'"
            :disabled="isDisabled || isProcessing"
            @click="$emit('click')"
          >
            <Loader2 v-if="isProcessing" class="animate-spin" />
            {{ linkText }}
          </Button>
        </div>
        <slot
          v-if="$slots.linkBtn"
          name="linkBtn"
          :class="['flex max-h-fit w-full bg-white p-5', !textTitle && 'sm:w-auto']"
        >
        </slot>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
<style>
@reference "@/input.css";

.p-dialog-header {
  @apply border-b;
  --p-dialog-header-padding: 16px 20px;
}
.p-dialog-title {
  --p-dialog-title-font-size: 16px;
}
.p-dialog-content {
  --p-dialog-content-padding: 20px;
}
.p-dialog-footer {
  --p-dialog-footer-padding: 20px;
}

/* Full screen mode styles */
.\!min-h-full {
  @apply flex flex-col;
  max-height: 100% !important;
}
.\!min-h-full .p-dialog-header {
  @apply sticky top-0 z-10 bg-white dark:bg-gray-900;
}
.\!min-h-full .p-dialog-content {
  @apply flex-1 flex flex-col;
}
.\!min-h-full .p-dialog-content > * {
  @apply flex-1;
}
</style>
